import { useState } from 'react';
import { Check, Dices, Loader2, Pencil, RotateCw, X } from 'lucide-react';
import { agenticAPI } from '../../../../services/api';
import { LivePulse, TurnClock } from './Liveness';

/**
 * Who is on this engagement.
 *
 * Nobody creates an agent by hand, and the roster is where that becomes
 * obvious rather than something you have to be told. The Lead appears the
 * moment the engagement is advanced; a pentester appears when the Lead assigns
 * it an approved hypothesis, and it is drawn a codename then.
 *
 * Agents that have finished stay listed. A roster that empties as work
 * completes reads as if the team went home, when in fact those are the runs
 * that produced the findings.
 */

const STATUS_TONE = {
  running: 'bg-wasabi-100 text-wasabi-800',
  refuting: 'bg-wasabi-100 text-wasabi-800',
  awaiting_authority: 'bg-amber-100 text-amber-800',
  awaiting_window: 'bg-amber-100 text-amber-800',
  awaiting_budget: 'bg-slate-200 text-slate-700',
  awaiting_slot: 'bg-slate-100 text-slate-600',
  //: NOT amber. Amber on this screen means "a human is holding this up", and
  //: the entire point of `awaiting_worker` is that nobody is.
  awaiting_worker: 'bg-slate-100 text-slate-600',
  awaiting_operator: 'bg-amber-100 text-amber-800',
  complete: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700',
  abandoned: 'bg-slate-100 text-slate-400',
};

// `running` means NOT PARKED, not "executing right now": between Celery
// slices a run sits `running` and idle, so a Lead that drafted a plan five
// hours ago and has not been advanced since reads as running throughout.
// Calling that "working" told an operator work was happening when nothing had
// happened since the morning. "In flight" is what the status actually asserts:
// this run is live and unblocked, whether or not a turn is executing this
// second.
const STATUS_LABEL = {
  running: 'in flight',
  refuting: 'under review',
  awaiting_authority: 'waiting on you',
  awaiting_window: 'outside the window',
  awaiting_budget: 'out of budget',
  awaiting_slot: 'waiting for a slot',
  //: The word matters. "Waiting" alone reads as blocked; "standing by" reads
  //: as deliberate, which is what it is. The Lead chose this.
  awaiting_worker: 'standing by',
  awaiting_operator: 'waiting to be read',
  complete: 'done',
  failed: 'failed',
  abandoned: 'stood down',
};

// A run that has never taken a turn is not "in flight" in any sense an
// operator means by it.
//
// `running` asserts "live and unblocked", which is true of an agent drawn
// four seconds ago and true of one that has been executing for ten minutes.
// Watched on pentest 44: two workers sat at 0/40 turns reading "in flight"
// while the banner correctly said "1 agent working", and the two statements
// could not be reconciled from the screen. This is also the shape of the
// pentest 34 bug, where two agents held "in flight" at zero turns for three
// days.
function statusLabel(member) {
  if (member.status === 'running' && !member.turns_used) return 'starting';
  return STATUS_LABEL[member.status] || member.status;
}

//: Why a blocked or parked agent is where it is. The Blocked chip carried no
//: explanation at all, so an operator clicked it repeatedly expecting one.
function statusTitle(member) {
  if (member.blocked_reason) return member.blocked_reason;
  if (member.status === 'running' && !member.turns_used) {
    return 'Drawn and assigned, but it has not taken its first turn yet. '
      + 'The scheduler picks it up within a minute.';
  }
  if (member.status === 'running') return 'Live and unblocked.';
  if (member.status === 'awaiting_worker') {
    return 'Nothing to do until a verdict lands, so it is waiting rather than '
      + 'asking you for anything. It wakes itself when the angle concludes.';
  }
  return STATUS_LABEL[member.status] || '';
}

function Member({ member, pentestId, onChanged, now }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(member.codename || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isLead = member.role === 'lead';

  const rename = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      await agenticAPI.setCodename(pentestId, member.run_id, payload);
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not rename it.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-slate-100 p-2.5 last:border-0">
      <div className="flex items-start gap-2.5">
        <div
          className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[10px] font-bold text-white ${
            isLead
              ? 'bg-gradient-to-br from-wasabi-500 to-wasabi-700'
              : 'bg-gradient-to-br from-slate-500 to-slate-700'
          }`}
        >
          {member.initials}
        </div>

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="CuriousFox"
                className="w-full rounded border border-slate-300 px-1.5 py-0.5 text-xs focus:border-wasabi-500 focus:outline-none"
              />
              <button type="button" disabled={busy} title="Save"
                      onClick={() => rename({ codename: name.trim() })}
                      className="text-wasabi-700 hover:text-wasabi-800 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button type="button" title="Cancel"
                      onClick={() => { setEditing(false); setName(member.codename || ''); }}
                      className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="truncate text-xs font-semibold text-slate-900">
                {member.display_name}
              </span>
              {!isLead && (
                <>
                  <button type="button" title="Rename" onClick={() => setEditing(true)}
                          className="text-slate-300 hover:text-slate-600">
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button type="button" title="Draw a new name" disabled={busy}
                          onClick={() => rename({ reroll: true })}
                          className="text-slate-300 hover:text-slate-600 disabled:opacity-50">
                    <Dices className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          )}

          <div className="mt-0.5 truncate text-[11px] text-slate-500">
            {isLead
              ? 'Plans, assigns, reads every verdict, reports to you'
              : (member.working_on || 'nothing yet')}
          </div>

          {error && <div className="mt-0.5 text-[11px] text-red-600">{error}</div>}

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[member.status] || 'bg-slate-100 text-slate-500'}`}
              title={statusTitle(member)}
            >
              {/* Motion on the row that is live, and only on that row. A
                  roster where six finished agents and one working agent are
                  drawn identically is a roster you have to read to use. */}
              {member.status === 'running' && <LivePulse className="h-1.5 w-1.5" />}
              {statusLabel(member)}
            </span>
            {/* Turns AND time on the current turn. `turns_used` steps once
                every couple of minutes; the clock is what says "now". */}
            <TurnClock run={member} now={now} className="text-[10px] text-slate-400" />
            <span className="text-[10px] text-slate-400">turns</span>
          </div>

          {member.blocked_reason && (
            <div className={`mt-1 text-[11px] leading-snug ${
              member.status === 'failed' ? 'text-red-700' : 'text-amber-800'
            }`}>
              {member.blocked_reason}
            </div>
          )}

          {isLead && member.status === 'failed' && (
            <div className="mt-1.5">
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await agenticAPI.restartLead(pentestId);
                    await onChanged?.();
                  } catch (e) {
                    setError(e?.response?.data?.detail || 'Could not restart the Lead.');
                  } finally {
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                Restart the Lead
              </button>
              <p className="mt-1 text-[11px] leading-snug text-slate-500">
                Gives it a fresh run. Nothing else is lost: its context is rebuilt
                from the angles, plan and evidence every turn.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TeamRoster({ roster, pentestId, onChanged, now }) {
  if (!roster?.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-3 text-[11px] leading-relaxed text-slate-500">
        No agents yet. Run the next step and the engagement gets a Lead, which reads the
        scope and your existing findings and drafts a plan. Pentesters are drawn
        and named when you approve it.
      </div>
    );
  }

  const workers = roster.filter((m) => m.role !== 'lead');

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      {roster.map((member) => (
        <Member
          key={member.run_id}
          member={member}
          pentestId={pentestId}
          onChanged={onChanged}
          now={now}
        />
      ))}
      {workers.length === 0 && (
        <p className="border-t border-slate-100 p-2.5 text-[11px] leading-relaxed text-slate-500">
          No pentesters yet. They are created, and named, when the Lead assigns an
          approved angle. Nothing is assigned before you approve the plan.
        </p>
      )}
    </div>
  );
}
